<template lang="pug">
div(:class="$vuetify.breakpoint.mobile ? 'pa-0' : 'pa-8'")
  div(:class="$vuetify.breakpoint.mobile ? 'd-flex align-center flex-nowrap overflow-x-auto gap-2 mb-4' : ''")
    range-dropdown-mobile(v-if="dates && $vuetify.breakpoint.mobile")
    div.d-flex.align-center.gap-2(:class="$vuetify.breakpoint.mobile ? '' : 'mb-6'")
      template(v-if="!$vuetify.breakpoint.mobile")
        div.text-3xl.font-semibold Patients
        v-spacer
      company-dropdown(v-if="isHq")
      provider-dropdown.mr-2
      range-dropdown(v-if="dates && !$vuetify.breakpoint.mobile")

  template(v-if="dates")
    v-row(:no-gutters="$vuetify.breakpoint.mobile")
      v-col(cols="12" md="6" lg="4" :class="[{ 'mb-2':$vuetify.breakpoint.mobile }]")
        pie-chart-card(title="Age" :data="ageData" :loading="isFetchingTrends")
      v-col(cols="12" md="6" lg="4" :class="[{ 'mb-2':$vuetify.breakpoint.mobile }]")
        pie-chart-card(title="Gender" :data="genderData" :loading="isFetchingTrends")
      v-col(cols="12" md="6" lg="4")
        pie-chart-card(title="Walk-in vs appointment" :data="visitData" :loading="isFetchingTrends")
    v-row
      v-col(cols="12" lg="6")
        horizontal-bar-card(title="Total visits" :loading="isFetchingAnalytics" :data="totalVisitsData" type="patients")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'
import { rangeValueFromPeriod } from '@/utils'
import RangeDropdown from './RangeDropdown.vue'
import RangeDropdownMobile from './RangeDropdownMobile.vue'
import ProviderDropdown from './ProviderDropdown.vue'
import PieChartCard from './PieChartCard.vue'
import HorizontalBarCard from './HorizontalBarCard.vue'
import CompanyDropdown from '@/components/hq/custom/CompanyDropdown.vue'

export default {
  name: 'InsightPatients',
  components: { RangeDropdown, RangeDropdownMobile, ProviderDropdown, PieChartCard, HorizontalBarCard, CompanyDropdown },
  data: () => ({
    isFetchingTrends: true,
    isFetchingAnalytics: true,
    genderData: null,
    ageData: null,
    visitData: null,
    totalVisitsData: null,
    trends: {},
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      dates: 'insight/getDates',
      beginDate: 'insight/getBeginDate',
      untilDate: 'insight/getUntilDate',
      selectedProviders: 'insight/getSelectedProviders',
      selectedCompanies: 'insight/getSelectedCompanies',
      currentDateRange: 'insight/getCurrentDateRange',
      isHq: 'hq/getIsHq'
    }),
  },
  watch: {
    dates () {
      this.fetchData()
    },
    selectedProviders: {
      handler() {
        this.fetchData()
      },
      deep: true
    },
    selectedCompanies: {
      handler() {
        this.fetchData()
      },
      deep: true
    }
  },
  created () {
    this.setupDates()
  },
  methods: {
    ...mapActions(['showMessage']),
    setupDates () {
      if (this.dates) {
        this.fetchData()
        return
      }
      const now = DateTime.local()
      const dateFormat = 'yyyy-MM-dd'
      const start = now.minus({ days: 6 })
      this.$store.commit('insight/setDates', [start.toFormat(dateFormat), now.toFormat(dateFormat)])
    },
    fetchData () {
      this.fetchTrends()
      this.fetchAnalytics()
    },
    fetchTrends (stopRefetch) {
      this.isFetchingTrends = true
      const params = {
        begin: this.beginDate,
        until: this.untilDate,
        range: rangeValueFromPeriod(this.currentDateRange?.id)
      }

      // Handle HQ vs regular company logic
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }

      if (this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/trends/${this.company.slug}/patients/segments`, { params })
        .then(resp => {
          const data = resp.data
          this.trends = data
          const { age, gender, visit_type } = data
          this.ageData = {
            labels: ['0-17', '18-25 y/o', '26-40 y/o', '41-59 y/o', '60+ y/o'],
            values: [age.lte_17, age.lte_25, age.lte_40, age.lte_59, age.gte_60]
          }
          this.genderData = {
            labels: ['Male', 'Female'],
            values: [gender.male, gender.female]
          }
          this.visitData = {
            labels: ['Walk-in', 'Appointment'],
            values: [visit_type.walk_in, visit_type.appointment]
          }
          this.isFetchingTrends = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchTrends(true)
          } else {
            this.isFetchingTrends = false
          }
        })
    },
    fetchAnalytics (stopRefetch) {
      this.isFetchingAnalytics = true
      const params = {
        begin: this.beginDate,
        until: this.untilDate,
        range: rangeValueFromPeriod(this.currentDateRange?.id)
      }

      // Handle HQ vs regular company logic
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }

      if (this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/trends/${this.company.slug}/patients/analytics`, { params })
        .then(resp => {
          const data = resp.data
          const visits = data.visits
          if (visits) {
            const obj = {
              labels: ['Once', 'Twice', '3 times', '4 times', '> 5 times'],
              values: [visits.x1_total, visits.x2_total, visits.x3_total, visits.x4_total, visits.gte5_total],
              percentage: [visits.x1_pct, visits.x2_pct, visits.x3_pct, visits.x4_pct, visits.gte5_pct],
            }
            this.totalVisitsData = obj
          }
          this.isFetchingAnalytics = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchAnalytics(true)
          } else {
            this.isFetchingAnalytics = false
          }
        })
    },
  }
}
</script>